import React, { useRef, useState } from 'react';

import {
	DynamicIconWrapper,
	Icons,
	SearchInput,
	Tooltip,
} from '@components/common';
import FilterMenu from '@components/common/Table/MultiSearch/FilterMenu';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { ITableSearch } from '@interfaces/components/table';

import { defaultSearchTooltipText } from '@components/common/Table/constants';

import styles from './MultiSearch.module.scss';

interface IMultiSearchProps {
	searchClassname?: string;
	searchContainerClassname?: string;
	delay?: number;
	disabled?: boolean;
	isLoading?: boolean;
	onFocusIn?: () => void;
	searchThreshold?: number;
	search: Array<ITableSearch>;
	isOpenSearch: boolean;
	searchWidth?: string;
}

const MultiSearch = ({
	searchClassname,
	searchContainerClassname,
	delay,
	disabled,
	isLoading,
	onFocusIn,
	searchThreshold,
	search,
	isOpenSearch,
	searchWidth = '320px',
}: IMultiSearchProps) => {
	const isMultiMode = search.length > 1;
	const [selectedSearch, setSelectedSearch] = useState(search[0]);
	const [isOpenFilter, setIsOpenFilter] = useState(false);
	const [isShowSearch, setIsShowSearch] = useState(isOpenSearch);

	const filterRef = useRef<HTMLDivElement | null>(null);
	const filterIconRef = useRef<HTMLDivElement | null>(null);

	const searchStyle = { width: `${searchWidth}` };

	const showSearch = () => {
		setIsShowSearch(true);
	};

	const hideSearch = () => {
		setIsShowSearch(false);
	};

	const toggleFilter = () => {
		setIsOpenFilter(prevState => !prevState);
	};

	useOnClickOutside({ refs: [filterRef, filterIconRef], handler: () => isOpenFilter && toggleFilter() });

	const handleChangeFilter = (value: string) => {
		search.forEach(({ handler }) => handler(''));

		setSelectedSearch(search.find(filterItem => filterItem.name === value) || search[0]);
	};

	return (
		<div className={styles.searchBar}>
			{isShowSearch
				? <div style={searchStyle}>
					{search.map(searchItem => {
						const placeholder = searchItem?.placeholder
							|| searchItem?.tooltipText || defaultSearchTooltipText;
						const tooltipContent = searchItem?.tooltipText || defaultSearchTooltipText;

						const handleHideSearch = () => {
							if (searchItem.initialValue.length > 0) {
								return;
							}

							hideSearch();
						};

						return (searchItem.name == selectedSearch.name &&
							<Tooltip
								key={`multisearch_tooltip_${searchItem.name}`}
								content={tooltipContent}
							>
								<div>
									<SearchInput
										key={searchItem.name}
										classNames={searchClassname}
										containerClassname={searchContainerClassname}
										delay={delay}
										disabled={disabled}
										hideSearch={handleHideSearch}
										isAutoFocus
										isClearable
										isLoading={isLoading}
										isNumber={searchItem?.isNumber}
										onFocusIn={onFocusIn}
										onSearch={searchItem.handler}
										placeholder={placeholder}
										searchThreshold={searchThreshold}
										value={searchItem.initialValue}
									>
										{isMultiMode
											&& (
												<span
													ref={filterIconRef}
													onClick={toggleFilter}
												>
													{Icons.searchFilter()}
												</span>
											)
										}
									</SearchInput>
								</div>
							</Tooltip>
						);
					})}
				</div>
				: <Tooltip
					content={search[0]?.tooltipText || defaultSearchTooltipText}
					placement={isShowSearch ? 'top-end' : 'top-start'}
				>
					<div
						className={styles.icon}
						onClick={showSearch}
					>
						<DynamicIconWrapper
							icon={Icons.search}
							isDisabled={disabled}
						/>
					</div>
				</Tooltip>
			}
			{isOpenFilter && isMultiMode
				&& <FilterMenu
					filterRef={filterRef}
					handleChangeFilter={handleChangeFilter}
					search={search}
					selectedSearch={selectedSearch}
				/>
			}
		</div>
	);
};

export default MultiSearch;
